/**
 * Aggregate ROSTER_ENTRY ratings for a parent's linked roster
 * entries. Fetches every rating row (RLS scopes it to the parent
 * via the `ratings_roster_entry_parent_read` policy from migration
 * 00085) and computes per-corner means.
 *
 * Returned shape: `Map<ratee_id, CornerComposite[]>` so callers can
 * render compact 4-corner tiles per row.
 *
 * Distinct from the PLAYER rating path (which respects the blind-
 * rating state machine) — ROSTER_ENTRY ratings are posted with
 * `state='REVEALED'` immediately, so the parent reads them as soon
 * as the coach saves.
 */
import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';
import { useAuth } from '../../../contexts/AuthContext';
import {
  computeRosterEntryCornerComposite,
  CORNER_IDS,
  type CornerId,
} from '@4cm/core/rosterEntry/rating';

export interface CornerComposite {
  corner_id: CornerId;
  mean: number | null;
  count: number;
}

interface State {
  data: Map<string, CornerComposite[]>;
  loading: boolean;
  error: string | null;
}

const EMPTY_BUCKET: CornerComposite[] = CORNER_IDS.map((c) => ({
  corner_id: c,
  mean: null,
  count: 0,
}));

export function useRosterEntryRatings(rateeIds: ReadonlyArray<string>): State & { refetch: () => void } {
  const { user, bypass } = useAuth();
  const [state, setState] = useState<State>({
    data: new Map(),
    loading: rateeIds.length > 0,
    error: null,
  });
  const [tick, setTick] = useState(0);

  // Join the ids into a stable cache key so we don't refetch on every
  // parent render when the array identity changes but contents don't.
  const idsKey = [...rateeIds].sort().join(',');

  useEffect(() => {
    let cancelled = false;
    if (!user || bypass || rateeIds.length === 0) {
      setState({ data: new Map(), loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));

    (async () => {
      const { data, error } = await supabase
        .from('ratings')
        .select('ratee_id, corner_id, score')
        .eq('ratee_type', 'ROSTER_ENTRY')
        .in('ratee_id', rateeIds as string[]);
      if (cancelled) return;
      if (error) {
        setState({ data: new Map(), loading: false, error: error.message });
        return;
      }
      const byRatee = new Map<string, Array<{ corner_id: string; score: number }>>();
      for (const row of (data ?? []) as Array<{ ratee_id: string; corner_id: string; score: number }>) {
        if (!byRatee.has(row.ratee_id)) byRatee.set(row.ratee_id, []);
        byRatee.get(row.ratee_id)!.push({ corner_id: row.corner_id, score: row.score });
      }

      const result = new Map<string, CornerComposite[]>();
      for (const id of rateeIds) {
        const rows = byRatee.get(id) ?? [];
        result.set(id, rows.length === 0 ? EMPTY_BUCKET : computeRosterEntryCornerComposite(rows));
      }
      setState({ data: result, loading: false, error: null });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, bypass, idsKey, tick]);

  return { ...state, refetch: () => setTick((t) => t + 1) };
}
